import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  updateDoc,
  onSnapshot 
} from 'firebase/firestore';
import { db } from '../firebase';

// Collection names
const LOCKERS_COLLECTION = 'lockers';

// Generate a random 4-digit PIN
export const generatePIN = () => {
  return Math.floor(1000 + Math.random() * 9000).toString();
};

// Initialize locker data in Firebase
export const initializeLockers = async () => {
  const locations = ['rajinder_nagar', 'patel_nagar'];
  
  for (const location of locations) {
    for (let i = 1; i <= 2; i++) {
      const lockerId = `locker${i}`;
      const lockerRef = doc(db, LOCKERS_COLLECTION, `${location}_${lockerId}`);
      
      try {
        const lockerDoc = await getDoc(lockerRef);
        if (!lockerDoc.exists()) {
          await setDoc(lockerRef, {
            location: location,
            lockerId: lockerId,
            status: 'empty',
            pin: '9999', // Default PIN - staff can change this
            customerData: null,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          });
        }
      } catch (error) {
        console.error('Error initializing locker:', error);
      }
    }
  }
};

// Get all locker status
export const getAllLockerStatus = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, LOCKERS_COLLECTION));
    const lockers = {};
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      const { location, lockerId } = data;
      
      if (!lockers[location]) {
        lockers[location] = {};
      }
      
      lockers[location][lockerId] = {
        status: data.status,
        pin: data.pin,
        customerData: data.customerData
      };
    });
    
    return lockers;
  } catch (error) {
    console.error('Error getting locker status:', error);
    return {};
  }
};

// Find customer by phone number
export const findCustomerByPhone = async (phoneNumber) => {
  try {
    const q = query(
      collection(db, LOCKERS_COLLECTION),
      where('customerData.phoneNumber', '==', phoneNumber)
    );
    
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      const data = doc.data();
      return {
        location: data.location,
        lockerId: data.lockerId,
        status: data.status,
        pin: data.pin,
        customerData: data.customerData
      };
    }
    
    return null;
  } catch (error) {
    console.error('Error finding customer:', error);
    return null;
  }
};

// Assign locker to customer (for customer drop-off)
export const assignLocker = async (location, lockerId, customerData) => {
  try {
    const lockerRef = doc(db, LOCKERS_COLLECTION, `${location}_${lockerId}`);
    const lockerDoc = await getDoc(lockerRef);
    
    if (lockerDoc.exists()) {
      const data = lockerDoc.data();
      if (data.status === 'empty') {
        // Use existing PIN - don't generate new one
        const pin = data.pin;
        
        await updateDoc(lockerRef, {
          status: 'full',
          pin: pin, // Keep the existing PIN
          customerData: {
            ...customerData,
            dropOffTime: new Date().toISOString(),
            type: 'customer_dropoff' // Mark as customer drop-off
          },
          updatedAt: new Date().toISOString()
        });
        
        return { lockerId, pin };
      }
    }
    
    return null;
  } catch (error) {
    console.error('Error assigning locker:', error);
    return null;
  }
};

// Assign locker for staff parcel (staff puts parcel in locker)
export const assignLockerForParcel = async (location, lockerId, phoneNumber) => {
  try {
    const lockerRef = doc(db, LOCKERS_COLLECTION, `${location}_${lockerId}`);
    const lockerDoc = await getDoc(lockerRef);
    
    if (lockerDoc.exists()) {
      const data = lockerDoc.data();
      if (data.status === 'empty') {
        // Use existing PIN - don't generate new one
        const pin = data.pin;
        
        await updateDoc(lockerRef, {
          status: 'full',
          pin: pin, // Keep the existing PIN
          customerData: {
            phoneNumber: phoneNumber,
            dropOffTime: new Date().toISOString(),
            type: 'staff_parcel' // Mark as staff parcel
          },
          updatedAt: new Date().toISOString()
        });
        
        return { lockerId, pin };
      }
    }
    
    return null;
  } catch (error) {
    console.error('Error assigning locker for parcel:', error);
    return null;
  }
};

// Mark locker as empty after pickup
export const markLockerEmpty = async (location, lockerId) => {
  try {
    const lockerRef = doc(db, LOCKERS_COLLECTION, `${location}_${lockerId}`);
    const lockerDoc = await getDoc(lockerRef);
    
    if (lockerDoc.exists()) {
      const data = lockerDoc.data();
      // Keep the existing PIN - don't generate a new one
      const existingPin = data.pin;
      
      await updateDoc(lockerRef, {
        status: 'empty',
        pin: existingPin, // Keep the same PIN
        customerData: null,
        updatedAt: new Date().toISOString()
      });
      
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('Error marking locker empty:', error);
    return false;
  }
};

// Update locker status (admin function)
export const updateLockerStatus = async (location, lockerId, status, pin = null) => {
  try {
    const lockerRef = doc(db, LOCKERS_COLLECTION, `${location}_${lockerId}`);
    const lockerDoc = await getDoc(lockerRef);
    
    if (lockerDoc.exists()) {
      const updateData = {
        status: status,
        updatedAt: new Date().toISOString()
      };
      
      if (pin) {
        updateData.pin = pin;
      }
      
      if (status === 'empty') {
        updateData.customerData = null;
      }
      
      await updateDoc(lockerRef, updateData);
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('Error updating locker status:', error);
    return false;
  }
};

// Real-time listener for locker updates
export const subscribeToLockerUpdates = (callback) => {
  return onSnapshot(collection(db, LOCKERS_COLLECTION), (snapshot) => {
    const lockers = {};
    
    snapshot.forEach((doc) => {
      const data = doc.data();
      const { location, lockerId } = data;
      
      if (!lockers[location]) {
        lockers[location] = {};
      }
      
      lockers[location][lockerId] = {
        status: data.status,
        pin: data.pin,
        customerData: data.customerData
      };
    });
    
    callback(lockers);
  });
}; 